import React, { useState } from 'react'
import { 
  Save, 
  Bell, 
  Shield, 
  Database, 
  Globe,
  Mail,
  Lock
} from 'lucide-react'

export default function Settings() {
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      push: false,
      sms: false
    },
    security: {
      twoFactor: false,
      sessionTimeout: 30,
      passwordPolicy: 'medium'
    },
    system: {
      maintenanceMode: false,
      autoBackup: true,
      logRetention: 90
    },
    general: {
      language: 'ko',
      timezone: 'Asia/Seoul',
      dateFormat: 'YYYY-MM-DD'
    }
  })

  const handleSave = () => {
    // 설정 저장 로직
    console.log('설정 저장:', settings)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">설정</h1>
        <p className="mt-1 text-sm text-gray-500">
          시스템 설정을 관리하고 구성하세요.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 알림 설정 */}
        <div className="card">
          <div className="flex items-center mb-4">
            <Bell className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">알림 설정</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">이메일 알림</label>
                <p className="text-xs text-gray-500">중요한 이벤트에 대한 이메일 알림</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.email}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, email: e.target.checked }
                })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">푸시 알림</label>
                <p className="text-xs text-gray-500">브라우저 푸시 알림</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.push}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, push: e.target.checked }
                })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">SMS 알림</label>
                <p className="text-xs text-gray-500">긴급 상황에 대한 SMS 알림</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.sms}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, sms: e.target.checked }
                })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
          </div>
        </div>

        {/* 보안 설정 */}
        <div className="card">
          <div className="flex items-center mb-4">
            <Shield className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">보안 설정</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">2단계 인증</label>
                <p className="text-xs text-gray-500">추가 보안을 위한 2단계 인증</p>
              </div>
              <input
                type="checkbox"
                checked={settings.security.twoFactor}
                onChange={(e) => setSettings({
                  ...settings,
                  security: { ...settings.security, twoFactor: e.target.checked }
                })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                세션 타임아웃 (분)
              </label>
              <input
                type="number"
                value={settings.security.sessionTimeout}
                onChange={(e) => setSettings({
                  ...settings,
                  security: { ...settings.security, sessionTimeout: parseInt(e.target.value) }
                })}
                className="input"
                min="5"
                max="480"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                비밀번호 정책
              </label>
              <select
                value={settings.security.passwordPolicy}
                onChange={(e) => setSettings({
                  ...settings,
                  security: { ...settings.security, passwordPolicy: e.target.value }
                })}
                className="input"
              >
                <option value="low">낮음</option>
                <option value="medium">보통</option>
                <option value="high">높음</option>
              </select>
            </div>
          </div>
        </div>

        {/* 시스템 설정 */}
        <div className="card">
          <div className="flex items-center mb-4">
            <Database className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">시스템 설정</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">유지보수 모드</label>
                <p className="text-xs text-gray-500">시스템 유지보수 중 사용자 접근 차단</p>
              </div>
              <input
                type="checkbox"
                checked={settings.system.maintenanceMode}
                onChange={(e) => setSettings({
                  ...settings,
                  system: { ...settings.system, maintenanceMode: e.target.checked }
                })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">자동 백업</label>
                <p className="text-xs text-gray-500">정기적인 데이터 백업</p>
              </div>
              <input
                type="checkbox"
                checked={settings.system.autoBackup}
                onChange={(e) => setSettings({
                  ...settings,
                  system: { ...settings.system, autoBackup: e.target.checked }
                })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                로그 보관 기간 (일)
              </label>
              <input
                type="number"
                value={settings.system.logRetention}
                onChange={(e) => setSettings({
                  ...settings,
                  system: { ...settings.system, logRetention: parseInt(e.target.value) }
                })}
                className="input"
                min="7"
                max="365"
              />
            </div>
          </div>
        </div>

        {/* 일반 설정 */}
        <div className="card">
          <div className="flex items-center mb-4">
            <Globe className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">일반 설정</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                언어
              </label>
              <select
                value={settings.general.language}
                onChange={(e) => setSettings({
                  ...settings,
                  general: { ...settings.general, language: e.target.value }
                })}
                className="input"
              >
                <option value="ko">한국어</option>
                <option value="en">English</option>
                <option value="ja">日本語</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시간대
              </label>
              <select
                value={settings.general.timezone}
                onChange={(e) => setSettings({
                  ...settings,
                  general: { ...settings.general, timezone: e.target.value }
                })}
                className="input"
              >
                <option value="Asia/Seoul">Asia/Seoul</option>
                <option value="UTC">UTC</option>
                <option value="America/New_York">America/New_York</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                날짜 형식
              </label>
              <select
                value={settings.general.dateFormat}
                onChange={(e) => setSettings({
                  ...settings,
                  general: { ...settings.general, dateFormat: e.target.value }
                })}
                className="input"
              >
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button onClick={handleSave} className="btn-primary flex items-center">
          <Save className="h-4 w-4 mr-2" />
          설정 저장
        </button>
      </div>
    </div>
  )
}
